<template>
  <div class="quiz-container">
    <!-- 题目加载中状态 -->
    <div v-if="isLoadingQuestions" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>正在加载题目...</p>
    </div>
    
    <!-- 只有在题目加载完成且有题目数据时才显示答题内容 -->
    <div v-else-if="questions.length > 0">
      <div class="quiz-header">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
        </div>
        <div class="progress-info">
          <span class="question-count">{{ currentQuestionIndex + 1 }}/{{ questions.length }}</span>
          <div class="question-dots">
            <div 
              v-for="(_, index) in questions" 
              :key="index"
              class="dot"
              :class="{ 'active': index === currentQuestionIndex, 'answered': answeredQuestions.includes(index) }"
            ></div>
          </div>
        </div>
      </div>

      <div class="quiz-card">
        <!-- 题目详情加载中状态 -->
        <div v-if="isLoadingDetail" class="loading-overlay small">
          <div class="loading-spinner"></div>
          <p>正在加载题目详情...</p>
        </div>
       
        <div v-else-if="this.currentQuestionDetail">
          <h2 class="question-text">{{ this.currentQuestionDetail.content }}</h2>
          <div class="options">
            <div 
              v-for="(option, index) in this.currentQuestionDetail.answerList" 
              :key="index"
              class="option"
              :class="{ 
                'selected': selectedOption === index || option.checked,
                'correct': showResult && option.checked
                // 'incorrect': showResult && !option.checked
              }"
              @click="selectOption(index)"
            >
              <span class="option-letter">{{ option.abc }}</span>
              <span class="option-text">{{ option.content }}</span>
              <span v-if="showResult && option.checked" class="option-icon correct-icon">✓</span>
              <!-- <span v-else-if="showResult && selectedOption === index && !option.checked" class="option-icon incorrect-icon">✗</span> -->
            </div>
          </div>

          <!-- <div class="feedback" v-if="showResult">
            <p v-if="isCorrect" class="correct-feedback">回答正确！</p>
            <p v-else class="incorrect-feedback">回答错误，正确答案是：{{ getOptionLetter(this.currentQuestionDetail.correct) }}</p>
          </div> -->

          <div class="navigation-buttons">
            <button 
              v-if="currentQuestionIndex > 0" 
              class="btn-secondary"
              @click="previousQuestion"
            >
              上一题
            </button>
            <button 
              v-else 
              class="btn-secondary disabled"
              disabled
            >
              上一题
            </button>
            
            <button 
              v-if="showResult && currentQuestionIndex < questions.length - 1" 
              class="btn-primary"
              @click="nextQuestion"
            >
              下一题
            </button>
            <button 
              v-else-if="showResult && currentQuestionIndex === questions.length - 1" 
              class="btn-primary"
              @click="finishQuiz"
            >
              完成答题
            </button>
            <button 
              v-else 
              class="btn-primary" 
              :disabled="selectedOption === null"
              @click="checkAnswer"
            >
              提交答案
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 题目加载失败状态 -->
    <div v-else class="error-state">
      <p>题目加载失败，请刷新页面重试</p>
    </div>

    <!-- 抽奖次数用尽提示模态框 -->
    <div v-if="showNoDrawsModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>提示信息</h3>
          <button class="close-btn" @click="closeNoDrawsModal">×</button>
        </div>
        <div class="modal-body">
          <p class="no-draws-text">很遗憾，您今日的抽奖次数已用完！</p>
          <p class="no-draws-tip">关注公众号每天可以参与抽大奖</p>
          
          <!-- 公众号二维码 -->
          <div class="qrcode-container">
            <img src="../images/jqr.jpg" alt="公众号二维码" style="width: 200px; height: 200px;"/>
          </div>
          
          <p class="follow-tip">扫描上方二维码关注公众号，获取更多抽奖机会！</p>
        </div>
        <div class="modal-footer">
          <button class="btn-primary" @click="closeNoDrawsModal">我知道了</button>
        </div>
      </div>
    </div>

    <!-- 加载遮罩 -->
    <div v-if="showLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>正在检查抽奖次数...</p>
    </div>
  </div>
</template>

<script>
import { lotteryAPI } from '../utils/api';
import globalStore from '../utils/globalStore.js';

export default {
  name: 'Quiz',
  data() {
    return {
      currentQuestionIndex: 0,
      selectedOption: null,
      showResult: false,
      correctAnswers: 0,
      answeredQuestions: [],
      showNoDrawsModal: false,
      showLoading: false,
      questions: [], // 初始化为空数组，将从API获取
      isLoadingQuestions: true, // 题目加载状态
      currentQuestionDetail: null, // 当前题目详细信息
      isLoadingDetail: false // 题目详情加载状态
    }
  },
  watch: {
    // 监听当前题目索引变化，获取新题目的详细信息
    currentQuestionIndex(newIndex) {
      this.fetchCurrentQuestionDetail(this.questions[newIndex]);
    }
  },
  created() {
    // 组件创建时从API获取题目数据
    this.fetchQuestions();
  },
  computed: {
    progressPercentage() {
      return ((this.currentQuestionIndex + 1) / this.questions.length) * 100
    }
  },
  methods: {
    async fetchQuestions() {
      try {
        // 显示加载状态
        this.isLoadingQuestions = true;
        // 调用API获取题目，传入URL参数
        const response = await lotteryAPI.getQuizQuestions(globalStore.getUrlParams());
        
        if (response.code==0 && response.data && response.data.radioList) {
          // 设置题目数据
          this.questions = response.data.radioList;
          // 获取第一个题目的详细信息
          if (this.questions.length > 0) {
            this.fetchCurrentQuestionDetail(this.questions[0]);
          }
        } else {
          //console.error('获取题目失败：', response.message || '返回数据格式错误');
          // 使用默认题目作为兜底
          this.useDefaultQuestions();
        }
      } catch (error) {
        //console.error('获取题目时发生错误：', error);
        // 发生错误时使用默认题目
        this.useDefaultQuestions();
      } finally {
        // 无论成功失败，都隐藏加载状态
        this.isLoadingQuestions = false;
      }
    },
    
    // 获取当前题目的详细信息
    async fetchCurrentQuestionDetail(question) {
      try {
        this.isLoadingDetail = true;
        // 准备请求参数
        const params = {
          ...globalStore.getUrlParams(),
          quId: question.quId,
          paperId: question.paperId
        };
        
        // 调用API获取题目详细信息
        const response = await lotteryAPI.getQuestionDetail(params);
        
        if (response.code==0 && response.data) {
          // 更新当前题目详细信息
          this.currentQuestionDetail = response.data;
          //console.log("题目详情jjj",this.currentQuestionDetail);
        }
      } catch (error) {
        //console.error('获取题目详情失败：', error);
        // 失败时使用本地存储的题目信息
        this.currentQuestionDetail = null;
      } finally {
        this.isLoadingDetail = false;
      }
    },
    
    useDefaultQuestions() {
      // 使用默认题目数据作为兜底
      this.questions = [
        {
          text: '以下哪个不是人工智能的主要分支？',
          options: ['机器学习', '深度学习', '自然语言处理', '网络安全'],
          correct: 3
        },
        {
          text: '机器人三定律是谁提出的？',
          options: ['爱因斯坦', '阿西莫夫', '霍金', '图灵'],
          correct: 1
        },
        {
          text: '深度学习中常用的激活函数是？',
          options: ['Sigmoid', 'ReLU', 'Tanh', '以上都是'],
          correct: 3
        },
        {
          text: '图灵测试的目的是什么？',
          options: ['测试计算机的速度', '测试计算机是否具有智能', '测试网络连接', '测试内存容量'],
          correct: 1
        },
        {
          text: '以下哪种是机器人的感知系统？',
          options: ['伺服电机', '传感器', '减速器', '控制器'],
          correct: 1
        }
      ];
      // 设置第一个题目的详细信息
      if (this.questions.length > 0) {
        this.currentQuestionDetail = this.questions[0];
      }
    },
    
    getOptionLetter(index) {
      return String.fromCharCode(65 + index)
    },
    selectOption(index) {
      if (!this.showResult) {
        // 先将所有选项的checked状态重置为false
        if (this.currentQuestionDetail && this.currentQuestionDetail.answerList) {
          this.currentQuestionDetail.answerList.forEach(option => {
            option.checked = false;
          });
        }
        // 然后设置当前选中的选项
        this.selectedOption = index;
      }
    },
    //提交答案
    async checkAnswer() {
      if (this.selectedOption === null) {
        alert('请选择一个答案');
        return;
      }
      
      try {
        this.isLoadingDetail = true;
        // 准备提交答案的参数
        const params = {
          ...globalStore.getUrlParams(),
          paperId: this.currentQuestionDetail.paperId,
          quId: this.currentQuestionDetail.quId,
          answer: '',
          answers:[this.currentQuestionDetail.answerList[this.selectedOption].id]
        };
        
        // 调用API提交答案
        const response = await lotteryAPI.submitAnswer(params);
        
        // 显示结果
        this.showResult = true;
        
        // 重置所有选项的checked状态
        if (this.currentQuestionDetail && this.currentQuestionDetail.answerList) {
          this.currentQuestionDetail.answerList.forEach((option, idx) => {
            option.checked = false;
          });
          // 标记选中的选项
          const selectedIndex = this.selectedOption;
          if (selectedIndex !== undefined && selectedIndex !== null && this.currentQuestionDetail.answerList[selectedIndex]) {
            this.currentQuestionDetail.answerList[selectedIndex].checked = true;
          }
        }
        
        // 根据API返回结果判断答案是否正确
        if (response.code==0 && this.currentQuestionIndex<this.questions.length-1) {
         // this.correctAnswers++;
          // 进入下一题
          this.nextQuestion();
        }
        
        // 记录已回答的题目索引
        if (!this.answeredQuestions.includes(this.currentQuestionIndex)) {
          this.answeredQuestions.push(this.currentQuestionIndex);
        }
      } catch (error) {
        //console.error('提交答案失败:', error);
        // 出错时使用本地判断
        this.showResult = true;
        // 重置所有选项的checked状态并标记选中的选项
        if (this.currentQuestionDetail && this.currentQuestionDetail.answerList) {
          this.currentQuestionDetail.answerList.forEach((option, idx) => {
            option.checked = false;
          });
          // 标记选中的选项
          const selectedIndex = this.selectedOption;
          if (selectedIndex !== undefined && selectedIndex !== null && this.currentQuestionDetail.answerList[selectedIndex]) {
            this.currentQuestionDetail.answerList[selectedIndex].checked = true;
          }
        }
        if (this.isCorrect) {
          this.correctAnswers++;
        }
        if (!this.answeredQuestions.includes(this.currentQuestionIndex)) {
          this.answeredQuestions.push(this.currentQuestionIndex);
        }
      } finally {
        this.isLoadingDetail = false;
      }
    },
    //下一题
    nextQuestion() {
      this.currentQuestionIndex++
      this.selectedOption = null
      this.showResult = false
    },
    //上一题
    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
        this.selectedOption = null;
        this.showResult = false;
        this.quizCompleted = false;
        // 确保所有选项的checked状态都被重置
        if (this.currentQuestionDetail && this.currentQuestionDetail.answerList) {
          this.currentQuestionDetail.answerList.forEach(option => {
            option.checked = false;
          });
        }
      }
    },
    //完成答题，提交结果，判断是否有抽奖次数
    async finishQuiz(formData) {
      formData.id=this.currentQuestionDetail.paperId;
      formData.openid=globalStore.getUrlParams().openid;
      const str =  await lotteryAPI.finishQuiz(formData);
      if (str.code==0 && str.data) {
        // 及格及以上
        this.showLoading = true;
        try {
          // const response = await lotteryAPI.getLotteryInfo(globalStore.getUrlParams());
          
          // if (response.code==0 && response.data.remainingDraws > 0) {
          //   // 还有抽奖次数，去抽奖页面
          //   this.$router.push('/lottery');
          // } else {
          //   // 没有抽奖次数了，显示提示模态框
          //   this.showNoDrawsModal = true;
          // }
          this.$router.push('/lottery');
        } catch (error) {
          //console.error('获取抽奖信息失败:', error);
          // 发生错误时默认允许抽奖
          this.$router.push('/lottery');
        } finally {
          this.showLoading = false;
        }
      } else {
        // 有答错的，去失败页面
        this.$router.push('/failure');
      }
    },
    closeNoDrawsModal() {
      this.showNoDrawsModal = false;
    }
  }
}
</script>

<style scoped>
.quiz-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%);
  padding: 20px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.quiz-header {
  max-width: 800px;
  margin: 0 auto 30px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 15px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  transition: width 0.5s ease;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.question-count {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.question-dots {
  display: flex;
  gap: 10px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
}

.dot.active {
  background: #4caf50;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.7);
  transform: scale(1.2);
}

.dot.answered {
  background: #2196f3;
}

.quiz-card {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.quiz-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #4caf50, #2196f3);
}

.question-text {
  font-size: 1.6rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
  line-height: 1.5;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.option {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.option:hover:not(.selected):not(.correct):not(.incorrect) {
  background: #e9ecef;
  transform: translateX(5px);
}

.option.selected {
  border-color: #2196f3;
  background: #e3f2fd;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.option.correct {
  border-color: #4caf50;
  background: #e8f5e9;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2);
}

.option.incorrect {
  border-color: #f44336;
  background: #ffebee;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.2);
}

.option-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
  margin-right: 15px;
  font-weight: 600;
  color: #666;
}

.option.selected .option-letter {
  background: #2196f3;
  color: white;
}

.option.correct .option-letter {
  background: #4caf50;
  color: white;
}

.option.incorrect .option-letter {
  background: #f44336;
  color: white;
}

.option-text {
  flex: 1;
  font-size: 1.1rem;
  color: #333;
}

.option-icon {
  font-size: 1.5rem;
  font-weight: bold;
  margin-left: 10px;
}

.correct-icon {
  color: #4caf50;
}

.incorrect-icon {
  color: #f44336;
}

.feedback {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
}

.correct-feedback {
  color: #4caf50;
  font-weight: 600;
  font-size: 1.2rem;
}

.incorrect-feedback {
  color: #f44336;
  font-weight: 600;
  font-size: 1.2rem;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-top: 30px;
}

.btn-primary {
  padding: 12px 30px;
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.btn-primary:disabled {
  background: #cccccc;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.btn-secondary {
  padding: 12px 30px;
  background: #f1f1f1;
  color: #666;
  border: none;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary:hover:not(.disabled) {
  background: #e0e0e0;
  transform: translateY(-2px);
}

.btn-secondary.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 15px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
  text-align: center;
}

.no-draws-text {
  font-size: 1.3rem;
  font-weight: 600;
  color: #f44336;
  margin-bottom: 15px;
}

.no-draws-tip {
  color: #666;
  margin-bottom: 20px;
}

.qrcode-container {
  margin: 20px 0;
}

.follow-tip {
  color: #333;
  font-size: 1rem;
  margin-top: 15px;
}

.modal-footer {
  padding: 20px;
  text-align: center;
  border-top: 1px solid #eee;
}

/* 加载状态样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  z-index: 999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 小尺寸加载提示 - 用于题目详情加载 */
.loading-overlay.small {
  position: relative;
  background: rgba(255, 255, 255, 0.8);
  min-height: 200px;
  border-radius: 10px;
  margin: 20px 0;
}

.loading-overlay.small .loading-spinner {
  width: 30px;
  height: 30px;
  border-width: 3px;
  margin-bottom: 15px;
}

.loading-overlay.small p {
  font-size: 1rem;
  color: #666;
}

/* 错误状态样式 */
.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  flex-direction: column;
  color: #f44336;
  font-size: 1.2rem;
}
</style>